/**
 * What something shared into the app from outside turns into.
 *
 * The system hands over a bag: some files, maybe a line of text, in no
 * particular arrangement. What this app sends are messages of exactly four
 * kinds, so somebody has to decide which — and that decision is arithmetic over
 * a list rather than anything to do with a screen, which is why it lives here
 * and is tested without one.
 *
 * The rules are the ones the composer already follows, deliberately: pictures
 * and clips become one album even when there is only one of them (that is what
 * «Галерея» does today — see addToAlbum in components/Composer.tsx), anything
 * else is a file message, and a line of text with pictures under it is their
 * caption rather than a second bubble. A share should produce the message the
 * same content would have produced had it been picked from inside the app.
 */

import { MAX_ALBUM_ITEMS } from './album.ts';

/** One thing the system handed over. */
export interface SharedItem {
  /**
   * Where the shell put it — a path on the device, not bytes. A shared video is
   * tens of megabytes and the bridge is JSON; the same reasoning that brings a
   * кружочек back as a path applies here, and the page reads it through
   * `Capacitor.convertFileSrc`.
   */
  path: string;
  name: string;
  mime: string;
  size: number;
}

/** One arrival: everything the system handed over in a single act of sharing. */
export interface SharedPayload {
  /**
   * The shell's name for this arrival. It exists so the same share cannot be
   * acted on twice — a queue drained on mount and again on the next
   * `visibilitychange` must not put the photograph in the chat two times — and
   * so the files can be swept once the message is away.
   */
  id: string;
  items: SharedItem[];
  /** `EXTRA_TEXT`, an `NSURL`, a plain string: a line that came with it. */
  text: string | null;
}

/** What a share is about to become, once the arithmetic is done. */
export interface SharePlan {
  /** Pictures and clips, as one album message. Empty when there are none. */
  media: SharedItem[];
  /** Everything else, one file message each, in the order they arrived. */
  files: SharedItem[];
  /**
   * The words. The album's caption when there is an album, and a message of its
   * own when there is not — which is what a shared link is, since a link in
   * this app is text that the bubble draws as a link (see TextBody).
   */
  text: string | null;
  /** How many pictures were left out because ten is the cap. */
  dropped: number;
}

/**
 * Whether a shared file can be a tile in an album.
 *
 * Mirrors `albumKindOf`, and through it the server's own rule, on the one field
 * that survives the trip from the shell: an item here is a name, a size and a
 * mime type — there is no `File` yet, because the bytes are still on disk.
 */
export function isAlbumMime(mime: string): boolean {
  const lower = mime.toLowerCase();
  if (lower === 'image/svg+xml') return false;
  return lower.startsWith('image/') || lower.startsWith('video/');
}

/**
 * The messages one arrival becomes.
 *
 * The cap is applied here rather than by refusing the share: somebody who sent
 * fifteen photographs meant to send photographs, and ten of them arriving with
 * a line saying so is a better answer than none of them arriving.
 */
export function planShare(payload: SharedPayload): SharePlan {
  const media: SharedItem[] = [];
  const files: SharedItem[] = [];
  for (const item of payload.items) {
    (isAlbumMime(item.mime) ? media : files).push(item);
  }
  const text = payload.text?.trim() ? payload.text.trim() : null;
  return {
    media: media.slice(0, MAX_ALBUM_ITEMS),
    files,
    text,
    dropped: Math.max(0, media.length - MAX_ALBUM_ITEMS),
  };
}

/** Nothing to send: an arrival with no files and nothing said. */
export function isEmptyPlan(plan: SharePlan): boolean {
  return plan.media.length === 0 && plan.files.length === 0 && plan.text === null;
}

/**
 * The items the server would refuse, so the screen can say so before the upload
 * instead of after it.
 *
 * The same check the album's compose screen makes, and for the same reason:
 * finding out from a 413 means watching everything else go up behind the one
 * thing that was never going to be accepted. Only the size is knowable here —
 * a picked video is re-encoded on the device before it goes and can come out far
 * smaller, but a shared one is sent as it is, so its size on disk is its size on
 * the wire.
 */
export function oversized(plan: SharePlan, maxUploadBytes: number): SharedItem[] {
  return [...plan.media, ...plan.files].filter((item) => item.size > maxUploadBytes);
}

/**
 * Whether the whole of a shared line is one address.
 *
 * Only used to decide how the preview draws it — a link gets the card the canvas
 * asks for and a sentence gets the text. What is sent is the same message
 * either way, because in this app a link *is* text (see the README, «And so are
 * links»); there is nothing here to unfurl and no title to put on the card.
 */
export function isLinkOnly(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === '' || /\s/.test(trimmed)) return false;
  return /^https?:\/\/\S+$/i.test(trimmed);
}
